/**
 * Single source of truth for parsing API errors into user-facing text.
 *
 * Backend (DRF) standardises on:
 *   - `{"detail": "..."}` (custom + unhandled exceptions, see api/middleware/exception_handler.py)
 *   - `{"<field>": ["..."], ...}` (serializer validation)
 *   - `{"non_field_errors": ["..."]}` (cross-field validation)
 *
 * Axios surfaces transport-level failures with no `response` (timeout, network down).
 *
 * Callers should never reach into `error.response.data` directly; use this.
 */
import { AxiosError } from 'axios'

export interface ApiErrorInfo {
  /** Human-readable single sentence safe to render in a toast. */
  detail: string
  /** HTTP status when available. */
  status?: number
  /** DRF field-level validation errors, for forms that want inline display. */
  fieldErrors?: Record<string, string[]>
  /**
   * Machine-readable error code when the backend returns one alongside
   * `detail` (e.g. `fiscal_year_missing`, `fiscal_year_closed`). Lets callers
   * branch on the failure kind and drive an actionable prompt instead of a
   * plain toast.
   */
  code?: string
  /** The raw response body, for callers that need code-specific payloads. */
  data?: Record<string, unknown>
}

const NETWORK_MSG = 'Cannot reach the server. Check your connection and try again.'
const TIMEOUT_MSG = 'The request timed out. Please try again.'
const FALLBACK_MSG = 'Something went wrong. Please try again.'

export function extractApiError(err: unknown): ApiErrorInfo {
  if (err instanceof AxiosError) {
    if (!err.response) {
      return { detail: err.code === 'ECONNABORTED' ? TIMEOUT_MSG : NETWORK_MSG }
    }
    const status = err.response.status
    const data = err.response.data as unknown

    const parsed = parseErrorBody(data, status)
    if (parsed) return parsed

    if (status >= 500) return { detail: 'Server error. Please try again in a moment.', status }
    if (status === 403) return { detail: 'You do not have permission to do that.', status }
    if (status === 404) return { detail: 'The requested resource was not found.', status }
    return { detail: err.message || FALLBACK_MSG, status }
  }

  if (err instanceof Error && err.message) return { detail: err.message }
  return { detail: FALLBACK_MSG }
}

/** Read a DRF error body (`detail`, `non_field_errors`, field errors). */
function parseErrorBody(data: unknown, status: number): ApiErrorInfo | null {
  if (!data || typeof data !== 'object') return null
  const rec = data as Record<string, unknown>

  const code = typeof rec.code === 'string' ? rec.code : undefined

  if (typeof rec.detail === 'string' && rec.detail.trim()) {
    return { detail: rec.detail, status, code, data: rec }
  }

  if (Array.isArray(rec.non_field_errors) && rec.non_field_errors.length > 0) {
    const first = rec.non_field_errors[0]
    if (typeof first === 'string') return { detail: first, status }
  }

  const fieldErrors: Record<string, string[]> = {}
  let firstFieldMsg: string | undefined
  for (const [key, value] of Object.entries(rec)) {
    if (key === 'detail' || key === 'non_field_errors') continue
    if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
      fieldErrors[key] = value as string[]
      if (!firstFieldMsg) firstFieldMsg = `${humanizeField(key)}: ${value[0]}`
    }
  }
  if (Object.keys(fieldErrors).length > 0) {
    return { detail: firstFieldMsg ?? 'Validation failed.', status, fieldErrors }
  }
  return null
}

function humanizeField(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}
